/** UAT administrator draft controls; generation never activates a questionnaire.
 * Keep existing V1 simulation separate from Meta asset validation and publication.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import FlowTestPanel from './FlowTestPanel';
import FlowDiagnostics from './FlowDiagnostics';
import FlowOperationFailure, { flowFailureMessage } from './FlowOperationFailure';
import { Segment, Button, Message, Label } from 'semantic-ui-react';

const errors = {
  flows_disabled:
    'Select Flows and save the questionnaire before preparing a draft or sending Flow invitations.',
  candidate_changed_generate_again:
    'The questionnaire changed. Generate and validate its draft again before approval.',
  approval_not_activated:
    'Activation was not completed. Refresh the questionnaire and retry approval after checking its draft.',
  meta_candidate_not_ready: 'Meta has not confirmed this Flow is ready for publication.',
  publication_not_confirmed: 'Publication was not confirmed. Reconcile its Meta status before retrying.',
  publication_requires_operator_check: 'The previous publication request needs an operator check.',
  immutable_package_requires_operator_check:
    'The retained package needs an operator check before activation.',
  deactivate_before_generating: 'Deactivate the questionnaire before generating a draft.',
  translation_not_ready: 'Finish translating the questionnaire before generating a draft.',
  questionnaire_outside_pilot:
    'This environment still limits Flow authoring to pilot questionnaires. Ask a Wakamoso administrator to update the authoring service.',
  questionnaire_not_found:
    'Save the questionnaire before generating a draft. Deleted questionnaires cannot generate drafts.',
  invalid_questionnaire_target:
    'The questionnaire reference is invalid. Reopen it from the questionnaire list.',
  forbidden: 'Draft management currently requires a TenantZero administrator.',
  source_changed_during_generation: 'The questionnaire changed during generation. Generate it again.',
  preparation_interrupted: 'Preparation was interrupted. A Wakamoso administrator must check the Meta state before retrying.',
  preparation_enqueue_failed: 'Preparation could not start after several attempts. Retry preparation or contact a Wakamoso administrator.',
  operator_reconciliation_required: 'The previous operation needs an operator check before retrying.',
  generation_queue_uncertain: 'The request may have started. Refresh its status before trying again.',
};
const labels = {
  not_generated: 'Not generated',
  publication_queued: 'Approval queued',
  publishing: 'Publishing',
  published: 'Ready to test',
  published_outdated: 'Published; activation not completed',
  publication_uncertain: 'Publication needs reconciliation',
  queued: 'Queued',
  generating: 'Generating',
  preparing_review: 'Submitting for review',
  ready: 'Ready to test',
  outdated: 'Flow needs updating',
  validation_failed: 'Needs attention',
  failed: 'Needs attention',
  reconciliation_required: 'Operator check required',
};
const metaLabels = { DRAFT: 'Draft', PUBLISHED: 'Published', DEPRECATED: 'Retired' };
const metaColors = { DRAFT: 'blue', PUBLISHED: 'green', DEPRECATED: 'grey' };
function MetaStatus({ status }) {
  return (
    <Label color={metaColors[status] || 'grey'}>{metaLabels[status] || status || 'Not available'}</Label>
  );
}
export function FlowDraftPanel({
  questionnaire,
  token,
  isTenantZero,
  dirty = false,
  onDeliveryMode,
  beforeOperation,
  preparationFeedback,
  onPreparationFeedback,
  onQuestionnaireRefresh,
  children,
}) {
  const updateInFlight = useRef(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const polling = useRef({ operationId: null, started: 0 });
  const enabled =
    process.env.REACT_APP_FLOW_DRAFT_ADMIN === 'true' && questionnaire.id && token;
  const request = useCallback(
    async (method = 'get', saved = questionnaire) => {
      setBusy(true);
      setError('');
      try {
        const response = await axios({
          method,
          url: `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_ENV}/whatsapp-flows/draft`,
          params: { tenantId: saved.tenantId, questionnaireId: saved.id },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResult(response.data);
        if (method === 'get' && response.data.questionnaireStatus &&
            (response.data.questionnaireStatus !== saved.status || response.data.active !== saved.isActive)) {
          onQuestionnaireRefresh?.(saved);
        }
      } catch (e) {
        const code = e.response?.data?.error;
        setError(
          flowFailureMessage(e.response?.data, errors[code] || 'Could not retrieve the draft operation. Refresh its status before retrying.'),
        );
      } finally {
        setBusy(false);
      }
    },
    [questionnaire.id, questionnaire.tenantId, questionnaire.updatedAt, questionnaire.status, questionnaire.isActive, token, onQuestionnaireRefresh],
  );
  useEffect(() => {
    if (!updateInFlight.current) setResult(null);
    if (enabled && !updateInFlight.current) request();
  }, [
    enabled,
    request,
    questionnaire.updatedAt,
    questionnaire.status,
    questionnaire.isActive,
    questionnaire.whatsappFlowsEnabled,
  ]);
  useEffect(() => {
    if (!enabled || (!result?.preparationPending && !['queued', 'generating', 'preparing_review', 'publication_queued', 'publishing'].includes(result?.status)))
      return undefined;
    if (polling.current.operationId !== result.operationId) {
      polling.current = { operationId: result.operationId, started: Date.now() };
    }
    // A stalled worker needs inspection; do not poll indefinitely in an open tab.
    if (Date.now() - polling.current.started > 900000) return undefined;
    const timer = setTimeout(() => request(), 5000);
    return () => clearTimeout(timer);
  }, [enabled, result, request]);
  useEffect(() => {
    if (enabled && preparationFeedback) request();
  }, [enabled, preparationFeedback, request]);
  useEffect(() => {
    if (typeof result?.whatsappFlowsEnabled === 'boolean') onDeliveryMode?.(result.whatsappFlowsEnabled);
  }, [result?.whatsappFlowsEnabled, onDeliveryMode]);
  const updateDraft = async () => {
    if (!isTenantZero || updateInFlight.current) return;
    updateInFlight.current = true;
    setBusy(true);
    try {
      const saved = beforeOperation ? await beforeOperation(result?.version ? 'Update draft' : 'Generate draft flow') : questionnaire;
      if (saved) {
        onPreparationFeedback?.(null);
        await request('post', saved);
      }
    } finally {
      updateInFlight.current = false;
      setBusy(false);
    }
  };
  const flowsEnabled = questionnaire.whatsappFlowsEnabled ?? result?.whatsappFlowsEnabled;
  if (!enabled)
    return (
      <>
        <Segment className="questionnaire-card flow-admin-panel">
          {children}
          <section id="whatsapp-flows-panel" aria-label="WhatsApp Flow preparation">
            {preparationFeedback?.pending && <p role="status">Preparing saved changes before submitting for approval…</p>}
            {preparationFeedback?.error && <Message visible negative content={preparationFeedback.error} />}
            <FlowDiagnostics diagnostics={preparationFeedback?.result?.diagnostics} ignoredItems={preparationFeedback?.result?.ignoredItems} />
          </section>
          <p>
            Flow management is available to Wakamoso administrators for saved questionnaires in enabled
            environments.
          </p>
        </Segment>
        <Segment className="questionnaire-card flow-admin-panel">
          <h3>Testing</h3>
          <p>
            Test invitations are available to Wakamoso administrators for saved questionnaires in enabled
            environments.
          </p>
        </Segment>
      </>
    );
  const active = questionnaire.isActive || questionnaire.status === 'active' || result?.active;
  const pending = result?.preparationPending || preparationFeedback?.pending || ['queued', 'generating', 'preparing_review', 'publication_queued', 'publishing'].includes(result?.status);
  const automatic = result?.automaticPreparationEnabled;
  const locked =
    // Unsaved edits cannot override saved delivery/publication restrictions.
    flowsEnabled === false ||
    active ||
    questionnaire.status === 'translating' ||
    busy ||
    pending ||
    (dirty && !beforeOperation) ||
    !result ||
    ['reconciliation_required', 'publication_uncertain'].includes(result?.status);
  const ready = !locked && result?.status === 'ready' && result?.metaStatus === 'DRAFT';
  const previous = ['validation_failed', 'failed', 'outdated'].includes(result?.status);
  const updated = result?.updatedAt?.replace(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/,
    '$1-$2-$3T$4:$5:$6Z',
  );
  const date = updated && new Date(updated);
  const binding = result?.currentBinding;
  const distinctBinding =
    binding?.mode?.toLowerCase() === 'published' &&
    binding.flowId !== result?.flowId &&
    (!binding.managementUrl || binding.managementUrl !== result?.managementUrl);
  const testReady =
    !busy &&
    !pending &&
    (!dirty || !!beforeOperation) &&
    !!result &&
    questionnaire.status !== 'translating' &&
    ((dirty && !!beforeOperation) ||
      flowsEnabled === false ||
      (active
        ? binding?.enabled && binding?.metaValidated && ['draft', 'published'].includes(binding?.mode)
        : ['ready', 'published'].includes(result?.status) &&
          ['DRAFT', 'PUBLISHED'].includes(result?.metaStatus)));
  const published = result?.metaStatus === 'PUBLISHED';
  const operationLabel = error ? 'Status unavailable' : result?.preparationPending ? 'Preparation queued' : labels[result?.status] || 'Checking status…';
  return (
    <>
      <Segment className="questionnaire-card flow-admin-panel">
        {children}
        <section aria-label="WhatsApp Flow preparation" id="whatsapp-flows-panel">
          <h4>Flow management</h4>
          <p>Manage the WhatsApp screens generated from your saved questionnaire.</p>
          <div className="flow-status-row" aria-live="polite">
            {result?.version && <strong>Version {result.version}</strong>}
            {result?.metaStatus && <MetaStatus status={result.metaStatus} />}
            <Label role="status" color={!error && (pending || !result) ? 'yellow' : 'grey'}>
              {operationLabel}
            </Label>
            {isTenantZero && result?.managementUrl && (
              <a href={result.managementUrl} target="_blank" rel="noopener noreferrer">
                Open in Meta
              </a>
            )}
          </div>
          <div className="flow-operations">
            {date && !isNaN(date.getTime()) && (
              <p className="flow-muted">Last operation: {date.toLocaleString()}</p>
            )}
            {flowsEnabled === false && (
              <Message
                visible
                info
                content="This service currently requires WhatsApp Flows to be saved as the delivery mode before generating or updating a draft. Existing Flow details remain available."
              />
            )}
            {published && !active && (
              <p>
                This published flow is unavailable to new respondents while the questionnaire is inactive.
              </p>
            )}
            {automatic && (
              <p>Saving questionnaire details prepares the Flow. After editing questions or translations, update the draft here when you are ready to test.</p>
            )}
            {!automatic && published && !active && (
              <p>To change these screens, save your changes and generate a new draft flow.</p>
            )}
            {active && (
              <Message
                visible
                info
                content="Deactivate and save this questionnaire before updating its draft flow."
              />
            )}
            {pending && (
              <p role="status">Preparing saved changes — this page checks progress automatically.</p>
            )}
            {preparationFeedback?.error && <Message visible negative content={preparationFeedback.error} />}
            {error && <Message visible negative content={error} />}
            {previous && !isTenantZero && <p>Correct the reported configuration and save again to prepare the updated Flow.</p>}
            {(previous || result?.errorCode) && <FlowOperationFailure
              result={result}
              fallback={errors[result?.errorCode] || 'Check the Flow details below. If no correction is shown, contact Wakamoso support with the operation reference.'}
            />}
            <FlowDiagnostics diagnostics={result?.diagnostics} ignoredItems={result?.ignoredItems} />
            <div className="flow-actions">
              {isTenantZero && <Button
                size="small"
                type="button"
                primary={!ready}
                disabled={locked}
                loading={busy && !pending}
                onClick={updateDraft}
              >
                {published
                  ? 'Generate new draft flow'
                  : result?.version || result?.managementUrl
                    ? 'Update draft'
                    : 'Generate draft flow'}
              </Button>}
              <Button size="small" type="button" basic disabled={busy} onClick={() => request()}>
                Check status
              </Button>
            </div>
          </div>
          {distinctBinding && (
            <div className="flow-previous-version">
              <div className="flow-status-row">
                <strong>{binding.version ? `Version ${binding.version}` : 'Published version'}</strong>
                <MetaStatus status="PUBLISHED" />
                {isTenantZero && binding.managementUrl && (
                  <a href={binding.managementUrl} target="_blank" rel="noopener noreferrer">
                    Open in Meta
                  </a>
                )}
              </div>
              <p>
                {active && binding.enabled && binding.metaValidated
                  ? 'Respondents receive this published version.'
                  : !active
                    ? 'This published flow is unavailable to new respondents while the questionnaire is inactive.'
                    : 'This published version is not enabled for new respondents.'}
              </p>
            </div>
          )}
          {(result?.operationId || result?.flowId || result?.errorCode) && (
            <details className="flow-technical">
              <summary>Support references</summary>
              {result.operationId && (
                <p>
                  Operation reference: <code>{result.operationId}</code>
                </p>
              )}
              {result.flowId && (
                <p>
                  Flow reference: <code>{result.flowId}</code>
                </p>
              )}
              {result.errorCode && (
                <p>
                  Error: <code>{result.errorCode}</code>
                </p>
              )}
            </details>
          )}
        </section>
      </Segment>
      {isTenantZero && <Segment className="questionnaire-card flow-admin-panel" id="whatsapp-test-panel">
        <h3>Testing</h3>
        <p>Send a test invitation to up to 10 WhatsApp numbers.</p>
        <p>
          Tests use the saved delivery mode:{' '}
          <strong>
            {flowsEnabled === false ? 'Messages' : flowsEnabled === true ? 'WhatsApp Flows' : 'Checking…'}
          </strong>
          .
        </p>
        <div className="flow-operations">
          {!testReady && !dirty && (
            <Message
              visible
              info
              content={
                flowsEnabled === false
                  ? 'Wait for the saved questionnaire to be ready before testing.'
                  : active
                    ? 'A validated respondent Flow is required before testing.'
                    : 'Generate a validated draft flow in Flow management before sending invitations.'
              }
            />
          )}
          <FlowTestPanel
            key={questionnaire.id}
            questionnaire={questionnaire}
            token={token}
            ready={testReady}
            beforeOperation={beforeOperation}
            inline
          />
        </div>
      </Segment>}
    </>
  );
}
export default connect((state) => {
  const session = state.authData?.[0]?.signInUserSession;
  return {
    token: session?.accessToken?.jwtToken,
    isTenantZero: (session?.accessToken?.payload?.['cognito:groups'] || []).includes('TenantZero'),
  };
})(FlowDraftPanel);
